import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { NorthStarKpi } from '@nexus/contracts';
import type {
  DashboardMode,
  CompetitorFilters,
  ExploreDateSelectionFields,
  MyBrandFilters,
} from '../types/dashboard.types';
import { DEFAULT_COMPETITOR_FILTERS, DEFAULT_MY_BRAND_FILTERS } from '../types/dashboard.types';
import { normalizeDateSelection, presetIdToLegacyToken } from '../lib/explore-date-presets';

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_SELECTED_CARDS = [0];
const DEFAULT_NORTH_STAR: NorthStarKpi = 'roas';

// ─── Store ────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nexus_dashboard_filters';
const PERSIST_VERSION = 4;

/** Product selections only — non-product filters live in the URL. */
export interface TenantProductFilters {
  industryProducts: string[];
  products: string[] | null;
}

interface HydrateFromAppliedPayload {
  mode: DashboardMode;
  competitorFilters: CompetitorFilters;
  myBrandFilters: MyBrandFilters;
}

interface DashboardFiltersState {
  mode: DashboardMode;
  /** Draft filters the header edits — drive option dropdowns and auto-seeding. */
  competitorFilters: CompetitorFilters;
  myBrandFilters: MyBrandFilters;
  /** Applied (committed) filters — the only thing that drives the KPI request. */
  appliedCompetitorFilters: CompetitorFilters;
  appliedMyBrandFilters: MyBrandFilters;
  /** Per-tenant product selections — not URL-backed (lists can be large). */
  byTenant: Record<string, TenantProductFilters>;
  /** Per-tenant NorthStar KPI selections — persisted independently of filter snapshots. */
  northStarByTenant: Record<string, NorthStarKpi>;
  /** Tenant whose snapshot is currently mirrored into the top-level fields (not persisted). */
  _activeTenantId: string | null;
  /** My Brand view-state: which KPI cards are expanded into the chart. */
  selectedCards: number[];
  /** My Brand view-state: the selected north-star KPI. Ephemeral — derived at runtime from northStarByTenant. */
  northStar: NorthStarKpi;
  setMode: (mode: DashboardMode) => void;
  setCompetitorFilters: (update: CompetitorFilters | ((prev: CompetitorFilters) => CompetitorFilters)) => void;
  setMyBrandFilters: (update: MyBrandFilters | ((prev: MyBrandFilters) => MyBrandFilters)) => void;
  /**
   * Draft-only date write. Tests and Discard-sensitive flows use this; the
   * picker commit path is `commitDateSelection`, which also copies the dates
   * onto applied so queries refetch without a second Apply.
   */
  setDateSelection: (mode: DashboardMode, selection: ExploreDateSelectionFields) => void;
  /**
   * Picker Apply: write the selection to the named mode's draft **and** applied.
   * Other dirty fields on that mode stay uncommitted. The other mode is untouched.
   */
  commitDateSelection: (mode: DashboardMode, selection: ExploreDateSelectionFields) => void;
  /** Commit the current draft to applied (fires the request) and persist products. */
  applyFilters: () => void;
  /** Revert the draft back to the last applied snapshot. */
  discardFilters: () => void;
  /** Replace draft + applied from URL-resolved filters (external navigation / deep link). */
  hydrateFromApplied: (payload: HydrateFromAppliedPayload) => void;
  setSelectedCards: (cards: number[]) => void;
  toggleCard: (index: number) => void;
  setNorthStar: (kpi: NorthStarKpi) => void;
  /** Seeds the DB-default NorthStar for a tenant on first browser visit. No-op if a per-tenant value already exists. */
  seedNorthStarDefault: (tenantId: string, kpi: NorthStarKpi) => void;
  resetForTenant: (
    tenantId: string | null,
    options?: { hydratingFromUrl?: boolean },
  ) => void;
}

function snapshotProducts(
  tenantId: string | null,
  products: TenantProductFilters,
  byTenant: Record<string, TenantProductFilters>,
) {
  if (!tenantId) return byTenant;
  return { ...byTenant, [tenantId]: products };
}

function isLegacyTenantSnapshot(value: unknown): value is {
  competitorFilters?: CompetitorFilters;
  myBrandFilters?: MyBrandFilters;
} {
  return value !== null && typeof value === 'object' && ('competitorFilters' in value || 'myBrandFilters' in value);
}

function migrateByTenant(raw: Record<string, unknown>): Record<string, TenantProductFilters> {
  const out: Record<string, TenantProductFilters> = {};
  for (const [tenantId, entry] of Object.entries(raw)) {
    if (!entry || typeof entry !== 'object') continue;
    if (isLegacyTenantSnapshot(entry)) {
      out[tenantId] = {
        industryProducts: entry.competitorFilters?.industryProducts ?? [],
        products: entry.myBrandFilters?.products ?? null,
      };
      continue;
    }
    const modern = entry as TenantProductFilters;
    out[tenantId] = {
      industryProducts: modern.industryProducts ?? [],
      products: modern.products ?? null,
    };
  }
  return out;
}

function withNormalizedDateFields<T extends ExploreDateSelectionFields>(filters: T): T {
  return { ...filters, ...normalizeDateSelection(filters) };
}

/**
 * Applies a date selection to one mode's filter object.
 *
 * `dateRange` is the query wire value. Presets with no legacy token — 14 days,
 * every season, custom — leave it at its prior value, so nothing downstream may
 * use it as a change signal; the drilldown sync keys on selection identity.
 */
function withDateSelection<T extends ExploreDateSelectionFields & { dateRange: string }>(
  prev: T,
  selection: ExploreDateSelectionFields,
): T {
  const normalized = normalizeDateSelection(selection);
  const token = presetIdToLegacyToken(normalized.activePresetId);
  return {
    ...prev,
    ...normalized,
    dateRange: token ?? prev.dateRange,
  };
}

export const useDashboardFiltersStore = create<DashboardFiltersState>()(
  persist(
    (set) => ({
      mode: 'competitors',
      competitorFilters: { ...DEFAULT_COMPETITOR_FILTERS },
      myBrandFilters: { ...DEFAULT_MY_BRAND_FILTERS },
      appliedCompetitorFilters: { ...DEFAULT_COMPETITOR_FILTERS },
      appliedMyBrandFilters: { ...DEFAULT_MY_BRAND_FILTERS },
      byTenant: {},
      northStarByTenant: {},
      _activeTenantId: null,
      selectedCards: [...DEFAULT_SELECTED_CARDS],
      northStar: DEFAULT_NORTH_STAR,
      setMode: (mode) => set({ mode }),
      setCompetitorFilters: (update) =>
        set((state) => ({
          competitorFilters: withNormalizedDateFields(
            typeof update === 'function' ? update(state.competitorFilters) : update,
          ),
        })),
      setMyBrandFilters: (update) =>
        set((state) => ({
          myBrandFilters: withNormalizedDateFields(
            typeof update === 'function' ? update(state.myBrandFilters) : update,
          ),
        })),
      setDateSelection: (mode, selection) =>
        set((state) =>
          mode === 'my-brand'
            ? { myBrandFilters: withDateSelection(state.myBrandFilters, selection) }
            : { competitorFilters: withDateSelection(state.competitorFilters, selection) },
        ),
      commitDateSelection: (mode, selection) =>
        set((state) => {
          if (mode === 'my-brand') {
            return {
              myBrandFilters: withDateSelection(state.myBrandFilters, selection),
              appliedMyBrandFilters: withDateSelection(state.appliedMyBrandFilters, selection),
            };
          }
          return {
            competitorFilters: withDateSelection(state.competitorFilters, selection),
            appliedCompetitorFilters: withDateSelection(state.appliedCompetitorFilters, selection),
          };
        }),
      applyFilters: () =>
        set((state) => ({
          appliedCompetitorFilters: state.competitorFilters,
          appliedMyBrandFilters: state.myBrandFilters,
          byTenant: snapshotProducts(
            state._activeTenantId,
            {
              industryProducts: state.competitorFilters.industryProducts,
              products: state.myBrandFilters.products,
            },
            state.byTenant,
          ),
        })),
      discardFilters: () =>
        set((state) => ({
          competitorFilters: state.appliedCompetitorFilters,
          myBrandFilters: state.appliedMyBrandFilters,
        })),
      hydrateFromApplied: ({ mode, competitorFilters, myBrandFilters }) => {
        const nextCompetitor = withNormalizedDateFields(competitorFilters);
        const nextMyBrand = withNormalizedDateFields(myBrandFilters);
        set({
          mode,
          competitorFilters: nextCompetitor,
          myBrandFilters: nextMyBrand,
          appliedCompetitorFilters: nextCompetitor,
          appliedMyBrandFilters: nextMyBrand,
        });
      },
      setSelectedCards: (selectedCards) => set({ selectedCards }),
      toggleCard: (index) =>
        set((state) => ({
          selectedCards: state.selectedCards.includes(index)
            ? state.selectedCards.filter((i) => i !== index)
            : [...state.selectedCards, index],
        })),
      setNorthStar: (northStar) =>
        set((state) => ({
          northStar,
          northStarByTenant: state._activeTenantId
            ? { ...state.northStarByTenant, [state._activeTenantId]: northStar }
            : state.northStarByTenant,
        })),
      seedNorthStarDefault: (tenantId, kpi) =>
        set((state) => {
          if (state.northStarByTenant[tenantId] !== undefined) return {};
          return {
            northStar: kpi,
            northStarByTenant: { ...state.northStarByTenant, [tenantId]: kpi },
          };
        }),
      resetForTenant: (tenantId, options) =>
        set((state) => {
          if (state._activeTenantId === tenantId) return {};

          const saved = tenantId ? state.byTenant[tenantId] : undefined;
          const northStar = (tenantId ? state.northStarByTenant[tenantId] : undefined) ?? DEFAULT_NORTH_STAR;

          // Deep link / shared URL: keep filters for the hydration effect to
          // populate; only restore product selections from the snapshot.
          if (options?.hydratingFromUrl) {
            if (!saved) {
              return { _activeTenantId: tenantId, northStar };
            }
            return {
              _activeTenantId: tenantId,
              northStar,
              competitorFilters: {
                ...state.competitorFilters,
                industryProducts: saved.industryProducts,
              },
              myBrandFilters: {
                ...state.myBrandFilters,
                products: saved.products,
              },
              appliedCompetitorFilters: {
                ...state.appliedCompetitorFilters,
                industryProducts: saved.industryProducts,
              },
              appliedMyBrandFilters: {
                ...state.appliedMyBrandFilters,
                products: saved.products,
              },
            };
          }

          // No URL filters: full reset so the previous tenant's non-product
          // selections never carry across tenants. Saved products are kept.
          const competitorFilters: CompetitorFilters = {
            ...DEFAULT_COMPETITOR_FILTERS,
            industryProducts:
              saved?.industryProducts ?? [...DEFAULT_COMPETITOR_FILTERS.industryProducts],
          };
          const myBrandFilters: MyBrandFilters = {
            ...DEFAULT_MY_BRAND_FILTERS,
            products: saved ? saved.products : DEFAULT_MY_BRAND_FILTERS.products,
          };
          return {
            _activeTenantId: tenantId,
            northStar,
            mode: state.mode,
            competitorFilters,
            myBrandFilters,
            appliedCompetitorFilters: competitorFilters,
            appliedMyBrandFilters: myBrandFilters,
          };
        }),
    }),
    {
      name: STORAGE_KEY,
      version: PERSIST_VERSION,
      storage: createJSONStorage(() => localStorage),
      migrate: (persisted, version) => {
        const state = persisted as Record<string, unknown>;
        if (version < PERSIST_VERSION && state.byTenant && typeof state.byTenant === 'object') {
          state.byTenant = migrateByTenant(state.byTenant as Record<string, unknown>);
        }
        if (version < 3) {
          for (const key of ['myBrandFilters', 'appliedMyBrandFilters'] as const) {
            const filters = state[key];
            if (
              filters &&
              typeof filters === 'object' &&
              Array.isArray((filters as MyBrandFilters).accounts) &&
              (filters as MyBrandFilters).accounts!.length === 0
            ) {
              (filters as MyBrandFilters).accounts = null;
            }
          }
        }
        if (version < 4) {
          // Clean break: drop legacy global northStar; introduce per-tenant map.
          // All tenants receive the DB-default NorthStar on first post-deploy visit.
          state.northStarByTenant = {};
          delete state.northStar;
        }
        delete state.mode;
        return state as unknown as DashboardFiltersState;
      },
      partialize: (state) => ({
        byTenant: state.byTenant,
        northStarByTenant: state.northStarByTenant,
        selectedCards: state.selectedCards,
      }),
    },
  ),
);
